import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Optional, Input, HostBinding, ContentChildren, NgModule } from '@angular/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/router';
import { NavigationEnd, RouterLink, RouterLinkWithHref } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as i2 from '@angular/cdk/a11y';

const defaultColor = 'default';
const defaultIconPosition = 'after';
class MLinkComponent {
    constructor(router, changeDetectorRef, _elementRef, _focusMonitor, link, linkWithHref) {
        this.router = router;
        this.changeDetectorRef = changeDetectorRef;
        this._elementRef = _elementRef;
        this._focusMonitor = _focusMonitor;
        this.link = link;
        this.linkWithHref = linkWithHref;
        this._matchOptions = this._getActiveMatchOptions();
        this._destroy$ = new Subject();
        this._activeExact = false;
        this._inline = false;
        this._disabled = false;
        this.color = defaultColor;
        this.iconPosition = defaultIconPosition;
        this._tabindex = 0;
        this.isActive = false;
        router.events
            .pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this._destroy$))
            .subscribe(() => this.update());
    }
    /** Whether route checking should be exact. Defaults to `false` */
    get activeExact() {
        return this._activeExact;
    }
    set activeExact(value) {
        this._activeExact = coerceBooleanProperty(value);
        this._matchOptions = this._getActiveMatchOptions();
        this.update();
    }
    /** Whether link should be displayed inline with the text. Defaults to false */
    get inline() {
        return this._inline;
    }
    set inline(value) {
        this._inline = coerceBooleanProperty(value);
    }
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    get tabindex() {
        return this.disabled ? -1 : this._tabindex || 0;
    }
    set tabindex(value) {
        this._tabindex = coerceNumberProperty(value);
    }
    get class() {
        return `m-link-${this.color || defaultColor} ${this.iconPosition === 'before' ? 'm-link-icon-before' : ''}`;
    }
    ngOnInit() {
        this._focusMonitor.monitor(this._elementRef);
    }
    ngAfterContentInit() {
        this.links?.changes.pipe(takeUntil(this._destroy$)).subscribe(() => this.update());
        this.linksWithHrefs?.changes.pipe(takeUntil(this._destroy$)).subscribe(() => this.update());
        this.update();
    }
    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this._elementRef);
        this._destroy$.next();
        this._destroy$.complete();
    }
    update() {
        if (!this.links || !this.linksWithHrefs || !this.router.navigated) {
            return;
        }
        Promise.resolve().then(() => {
            const hasActiveLinks = this.hasActiveLinks();
            if (this.isActive !== hasActiveLinks) {
                this.isActive = hasActiveLinks;
                this.changeDetectorRef.markForCheck();
            }
        });
    }
    hasActiveLinks() {
        return (this._isLinkActive() ||
            this._isLinkWithHrefActive() ||
            this.links.some(this._isActiveCheck) ||
            this.linksWithHrefs.some(this._isActiveCheck));
    }
    _isActiveCheck(link) {
        return this.router.isActive(link.urlTree, this._matchOptions);
    }
    _isLinkActive() {
        return Boolean(this.link && this._isActiveCheck(this.link));
    }
    _isLinkWithHrefActive() {
        return Boolean(this.linkWithHref && this._isActiveCheck(this.linkWithHref));
    }
    _getActiveMatchOptions() {
        return this.activeExact
            ? { paths: 'exact', queryParams: 'exact', fragment: 'ignored', matrixParams: 'ignored' }
            : { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' };
    }
}
MLinkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MLinkComponent, deps: [{ token: i1.Router }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i2.FocusMonitor }, { token: i1.RouterLink, optional: true }, { token: i1.RouterLinkWithHref, optional: true }], target: i0.ɵɵFactoryTarget.Component });
MLinkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: MLinkComponent, selector: "a[m-link]", inputs: { activeExact: "activeExact", inline: "inline", disabled: "disabled", color: "color", iconPosition: "iconPosition", tabindex: "tabindex" }, host: { properties: { "class.m-link-inline": "this.inline", "class.m-link-disabled": "this.disabled", "attr.tabindex": "this.tabindex", "class.m-link-active": "this.isActive", "class": "this.class" } }, queries: [{ propertyName: "links", predicate: RouterLink, descendants: true }, { propertyName: "linksWithHrefs", predicate: RouterLinkWithHref, descendants: true }], exportAs: ["m-link"], ngImport: i0, template: "<span class=\"m-link-content\">\n  <ng-content></ng-content>\n</span>\n<ng-container *ngIf=\"!inline\">\n  <div class=\"m-link-icon-container\">\n    <ng-content select=\"m-icon\"></ng-content>\n  </div>\n</ng-container>\n", styles: [":host{font-weight:var(--fontWeightRegular);font-size:var(--fontSizeBody02);line-height:var(--lineHeightBody02);color:var(--main);text-decoration:unset;transition:color .3s ease-out;display:inline-flex;align-items:baseline}:host.m-link-default{--link-color: var(--main)}:host.m-link-alert{--link-color: var(--linkColorAlert)}:host.m-link-warning{--link-color: var(--warning80)}:host.m-link-info{--link-color: var(--linkColorInfo)}:host.m-link-white{--link-color: var(--linkColorWhite)}:host.m-link-secondary{--link-color: var(--base)}:host.m-link-featured{--link-color: var(--linkColorFeatured)}:host .m-link-content{order:0}:host .m-link-icon-container{align-items:center;display:flex;align-self:center}:host:visited{color:var(--main)}:host:hover{color:var(--main80);cursor:pointer}:host:active,:host.m-link-active{color:var(--main90)}:host:focus{outline:none}:host.cdk-keyboard-focused{outline:2px solid var(--accent)}:host ::ng-deep m-icon{height:1rem;width:1rem;font-size:1rem;margin-left:.5rem}:host:not(.m-link-inline):not(.m-link-disabled){color:var(--link-color)}:host:not(.m-link-inline):not(.m-link-disabled):hover .m-link-content{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:var(--link-color)}:host:not(.m-link-inline):not(.m-link-disabled):hover .m-link-icon-container{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:transparent}:host:not(.m-link-inline):not(.m-link-disabled):active .m-link-content,:host:not(.m-link-inline):not(.m-link-disabled).m-link-active .m-link-content{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:var(--link-color)}:host:not(.m-link-inline):not(.m-link-disabled):active .m-link-icon-container,:host:not(.m-link-inline):not(.m-link-disabled).m-link-active .m-link-icon-container{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:transparent}:host.m-link-disabled{color:var(--linkColorDisabled);pointer-events:none}:host.m-link-icon-before .m-link-content{order:1}:host.m-link-icon-before .m-link-icon-container ::ng-deep m-icon{margin-left:0;margin-right:.5rem}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MLinkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'a[m-link]', exportAs: 'm-link', changeDetection: ChangeDetectionStrategy.OnPush, template: "<span class=\"m-link-content\">\n  <ng-content></ng-content>\n</span>\n<ng-container *ngIf=\"!inline\">\n  <div class=\"m-link-icon-container\">\n    <ng-content select=\"m-icon\"></ng-content>\n  </div>\n</ng-container>\n", styles: [":host{font-weight:var(--fontWeightRegular);font-size:var(--fontSizeBody02);line-height:var(--lineHeightBody02);color:var(--main);text-decoration:unset;transition:color .3s ease-out;display:inline-flex;align-items:baseline}:host.m-link-default{--link-color: var(--main)}:host.m-link-alert{--link-color: var(--linkColorAlert)}:host.m-link-warning{--link-color: var(--warning80)}:host.m-link-info{--link-color: var(--linkColorInfo)}:host.m-link-white{--link-color: var(--linkColorWhite)}:host.m-link-secondary{--link-color: var(--base)}:host.m-link-featured{--link-color: var(--linkColorFeatured)}:host .m-link-content{order:0}:host .m-link-icon-container{align-items:center;display:flex;align-self:center}:host:visited{color:var(--main)}:host:hover{color:var(--main80);cursor:pointer}:host:active,:host.m-link-active{color:var(--main90)}:host:focus{outline:none}:host.cdk-keyboard-focused{outline:2px solid var(--accent)}:host ::ng-deep m-icon{height:1rem;width:1rem;font-size:1rem;margin-left:.5rem}:host:not(.m-link-inline):not(.m-link-disabled){color:var(--link-color)}:host:not(.m-link-inline):not(.m-link-disabled):hover .m-link-content{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:var(--link-color)}:host:not(.m-link-inline):not(.m-link-disabled):hover .m-link-icon-container{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:transparent}:host:not(.m-link-inline):not(.m-link-disabled):active .m-link-content,:host:not(.m-link-inline):not(.m-link-disabled).m-link-active .m-link-content{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:var(--link-color)}:host:not(.m-link-inline):not(.m-link-disabled):active .m-link-icon-container,:host:not(.m-link-inline):not(.m-link-disabled).m-link-active .m-link-icon-container{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:transparent}:host.m-link-disabled{color:var(--linkColorDisabled);pointer-events:none}:host.m-link-icon-before .m-link-content{order:1}:host.m-link-icon-before .m-link-icon-container ::ng-deep m-icon{margin-left:0;margin-right:.5rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i2.FocusMonitor }, { type: i1.RouterLink, decorators: [{
                    type: Optional
                }] }, { type: i1.RouterLinkWithHref, decorators: [{
                    type: Optional
                }] }]; }, propDecorators: { activeExact: [{
                type: Input
            }], inline: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.m-link-inline']
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.m-link-disabled']
            }], color: [{
                type: Input
            }], iconPosition: [{
                type: Input
            }], tabindex: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.tabindex']
            }], isActive: [{
                type: HostBinding,
                args: ['class.m-link-active']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], links: [{
                type: ContentChildren,
                args: [RouterLink, { descendants: true }]
            }], linksWithHrefs: [{
                type: ContentChildren,
                args: [RouterLinkWithHref, { descendants: true }]
            }] } });

class MLinkModule {
}
MLinkModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MLinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MLinkModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: MLinkModule, declarations: [MLinkComponent], imports: [CommonModule], exports: [MLinkComponent] });
MLinkModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MLinkModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: MLinkModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MLinkComponent],
                    imports: [CommonModule],
                    exports: [MLinkComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MLinkComponent, MLinkModule };
